import React, { useState, useMemo, useCallback } from 'react';
import {
    FaPlus, FaTrash, FaPen, FaCheck, FaCreditCard, FaBolt, FaCalendarAlt, FaCog,
    FaTimes, FaExclamationTriangle, FaFire, FaBullseye, FaBan, FaGem,
    FaEllipsisH, FaUtensils, FaBus, FaShoppingBag, FaFilm, FaHeartbeat,
    FaGraduationCap, FaGasPump, FaWifi, FaMoneyBillWave, FaPiggyBank, FaWallet
} from 'react-icons/fa';
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    Cell, ReferenceLine
} from 'recharts';
import { addToCollection, updateInCollection, deleteFromCollection } from '../../hooks/useFirestore';

const FIXED_EXPENSE_CATEGORIES = [
    'Rent & Housing', 'Electricity', 'Internet & Phone', 'Insurance',
    'Subscriptions', 'Domestic Help', 'Gas', 'Water', 'Maintenance', 'Other'
];

const OPERATIONAL_CATEGORIES = [
    'Food & Grocery', 'Transport', 'Shopping', 'Entertainment',
    'Healthcare', 'Education', 'Fuel', 'Utilities', 'Cash', 'Other'
];

const OP_CAT_ICONS = {
    'Food & Grocery': <FaUtensils />, Transport: <FaBus />, Shopping: <FaShoppingBag />,
    Entertainment: <FaFilm />, Healthcare: <FaHeartbeat />, Education: <FaGraduationCap />,
    Fuel: <FaGasPump />, Utilities: <FaWifi />, Cash: <FaMoneyBillWave />, Other: <FaEllipsisH />,
};

function getMonthKey(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const ExpensesTab = ({
    L, CHART_COLORS, LIGHT_TOOLTIP,
    transactions, stats, fixedExpenses, emis, operationalExpenses,
    spendingTargets, updateSpendingTargets,
    calendarMode, selectedDate, dateRange, settings,
    wishlistItems,
}) => {
    const [expSubTab, setExpSubTab] = useState('fixed');
    const [showFixedForm, setShowFixedForm] = useState(false);
    const [showEMIForm, setShowEMIForm] = useState(false);
    const [showTargetForm, setShowTargetForm] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [opForm, setOpForm] = useState({ label: '', amount: '', category: 'Food & Grocery', date: new Date().toISOString().slice(0, 10) });
    const [opSaving, setOpSaving] = useState(false);

    const now = new Date();
    const currentMonth = getMonthKey(now);
    const selectedMonth = getMonthKey(selectedDate);

    // ── Paid fixed & EMI totals for selected month ─────────────────
    const fixedBreakdown = useMemo(() => {
        const paid = [], pending = [], skipped = [];
        (fixedExpenses || []).filter(e => e.isActive !== false).forEach(e => {
            const status = (e.monthlyStatus || {})[selectedMonth];
            if (status === 'paid') paid.push(e);
            else if (status === 'skipped') skipped.push(e);
            else pending.push(e);
        });
        return {
            paid, pending, skipped,
            paidTotal: paid.reduce((s, e) => s + (Number(e.amount) || 0), 0),
            pendingTotal: pending.reduce((s, e) => s + (Number(e.amount) || 0), 0),
            skippedTotal: skipped.reduce((s, e) => s + (Number(e.amount) || 0), 0),
        };
    }, [fixedExpenses, selectedMonth]);

    const emiBreakdown = useMemo(() => {
        const paid = [], pending = [];
        (emis || []).forEach(e => {
            const status = (e.monthlyStatus || {})[selectedMonth];
            if (status === 'paid') paid.push(e);
            else pending.push(e);
        });
        return {
            paid, pending,
            paidTotal: paid.reduce((s, e) => s + (Number(e.monthlyAmount) || 0), 0),
            pendingTotal: pending.reduce((s, e) => s + (Number(e.monthlyAmount) || 0), 0),
        };
    }, [emis, selectedMonth]);

    // ── One-time purchases (wishlist payments in period) ──────────
    const wishlistWithPayments = useMemo(() =>
        (wishlistItems || []).filter(i => (i.payments || []).length > 0),
    [wishlistItems]);

    // Period one-time total: sum of payments made within the selected dateRange
    const periodOneTimeTotal = useMemo(() => {
        return (wishlistItems || []).reduce((sum, item) => {
            return sum + (item.payments || []).filter(p => {
                const d = new Date(p.date);
                return d >= dateRange.start && d <= dateRange.end;
            }).reduce((s, p) => s + (Number(p.amount) || 0), 0);
        }, 0);
    }, [wishlistItems, dateRange]);

    // Overall one-time total paid (all time)
    const oneTimeTotalPaid = useMemo(() =>
        (wishlistItems || []).reduce((s, i) => s + (Number(i.totalPaid) || 0), 0),
    [wishlistItems]);

    // ── Period expenses (operational + debit txns) ─────────────────
    const periodOpAndTxnExpenses = useMemo(() => {
        const txns = (transactions || []).filter(t => {
            const isDebit = t.type !== 'credit' && t.type !== 'income' && t.type !== 'gain';
            if (!isDebit) return false;
            const txDate = new Date(t.date);
            return txDate >= dateRange.start && txDate <= dateRange.end;
        });
        const ops = (operationalExpenses || []).filter(e => {
            const d = new Date(e.date);
            return d >= dateRange.start && d <= dateRange.end;
        });
        return [...txns, ...ops];
    }, [transactions, operationalExpenses, dateRange]);

    const opAndTxnTotal = useMemo(() =>
        periodOpAndTxnExpenses.reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0),
    [periodOpAndTxnExpenses]);

    // Total expenses = operational/txn + paid fixed + paid EMI + period one-time
    const totalPeriodExpenses = opAndTxnTotal + fixedBreakdown.paidTotal + emiBreakdown.paidTotal + periodOneTimeTotal;

    // Period income for EBITDA
    const totalPeriodIncome = useMemo(() =>
        (transactions || []).filter(t => {
            const isCredit = t.type === 'credit' || t.type === 'income' || t.type === 'gain';
            if (!isCredit) return false;
            const txDate = new Date(t.date);
            return txDate >= dateRange.start && txDate <= dateRange.end;
        }).reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0),
    [transactions, dateRange]);

    const totalFixedMonthly = useMemo(() =>
        (fixedExpenses || []).filter(e => e.isActive !== false).reduce((s, e) => s + (Number(e.amount) || 0), 0),
    [fixedExpenses]);

    const periodOpExpenses = useMemo(() =>
        (operationalExpenses || []).filter(e => {
            const d = new Date(e.date);
            return d >= dateRange.start && d <= dateRange.end;
        }).reduce((s, e) => s + (Number(e.amount) || 0), 0),
    [operationalExpenses, dateRange]);

    const totalEMIMonthly = useMemo(() =>
        (emis || []).reduce((s, e) => s + (Number(e.monthlyAmount) || 0), 0),
    [emis]);

    const ebitda = totalPeriodIncome - totalPeriodExpenses;

    // ── Allocation lookup: which expenses are funded by income allocations ──
    const allocationsByItem = useMemo(() => {
        const map = {};
        (transactions || []).filter(t => t.type === 'credit' || t.type === 'income' || t.type === 'gain').forEach(t => {
            (t.allocations || []).filter(a => a.status !== 'skipped').forEach(a => {
                if (!map[a.itemId]) map[a.itemId] = { totalFunded: 0, sources: [] };
                map[a.itemId].totalFunded += Number(a.amount) || 0;
                map[a.itemId].sources.push({
                    label: t.description || t.label || 'Income',
                    date: t.date,
                    amount: Number(a.amount) || 0,
                });
            });
        });
        return map;
    }, [transactions]);

    // ── Savings Pool (income - allocations - operational) ────────
    const totalActiveAllocations = useMemo(() =>
        (transactions || []).filter(t => t.type === 'credit' || t.type === 'income' || t.type === 'gain')
            .reduce((sum, t) => sum + (t.allocations || []).filter(a => a.status !== 'skipped')
                .reduce((s, a) => s + (Number(a.amount) || 0), 0), 0),
    [transactions]);

    const totalSavings = totalPeriodIncome - totalActiveAllocations - periodOpExpenses;

    // ── Expense trend data ────────────────────────────────────────
    const trendData = useMemo(() => {
        const ds = {};
        periodOpAndTxnExpenses.forEach(t => {
            const d = (t.date || '').slice(0, 10);
            ds[d] = (ds[d] || 0) + Math.abs(Number(t.amount) || 0);
        });

        if (calendarMode === 'month') {
            const points = [];
            const d = new Date(dateRange.start);
            while (d <= dateRange.end) {
                const key = d.toISOString().slice(0, 10);
                points.push({ label: String(d.getDate()), expense: ds[key] || 0 });
                d.setDate(d.getDate() + 1);
            }
            return points;
        } else if (calendarMode === 'week') {
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const points = [];
            const d = new Date(dateRange.start);
            for (let i = 0; i < 7; i++) {
                const key = d.toISOString().slice(0, 10);
                points.push({ label: dayNames[d.getDay()], expense: ds[key] || 0 });
                d.setDate(d.getDate() + 1);
            }
            return points;
        } else {
            const points = [];
            for (let i = 6; i >= 0; i--) {
                const d = new Date(dateRange.start);
                d.setDate(d.getDate() - i + 3);
                const key = d.toISOString().slice(0, 10);
                points.push({ label: `${d.getDate()}/${d.getMonth() + 1}`, expense: ds[key] || 0 });
            }
            return points;
        }
    }, [periodOpAndTxnExpenses, calendarMode, dateRange]);

    // ── Category breakdown ────────────────────────────────────────
    const categoryData = useMemo(() => {
        const grouped = {};
        periodOpAndTxnExpenses.forEach(t => {
            const cat = t.category || 'Other';
            grouped[cat] = (grouped[cat] || 0) + Math.abs(Number(t.amount) || 0);
        });
        return Object.entries(grouped).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    }, [periodOpAndTxnExpenses]);

    const totalCategorySpend = useMemo(() => categoryData.reduce((s, c) => s + c.value, 0), [categoryData]);

    // ── Expense targets ───────────────────────────────────────────
    const dailyCap = spendingTargets?.dailyCap || 0;
    const weeklyCap = dailyCap * 7;
    const monthlyCap = dailyCap * 30;
    const currentTarget = calendarMode === 'day' ? dailyCap : calendarMode === 'week' ? weeklyCap : monthlyCap;
    const targetRemaining = currentTarget - opAndTxnTotal;
    const targetPct = currentTarget > 0 ? (opAndTxnTotal / currentTarget) * 100 : 0;

    // ── Daily expense map for streak + achievement ────────────────
    const dailyExpenseMap = useMemo(() => {
        const map = {};
        (transactions || []).filter(t => t.type !== 'credit' && t.type !== 'income' && t.type !== 'gain').forEach(t => {
            const d = (t.date || '').slice(0, 10);
            map[d] = (map[d] || 0) + Math.abs(Number(t.amount) || 0);
        });
        (operationalExpenses || []).forEach(e => {
            const d = (e.date || '').slice(0, 10);
            map[d] = (map[d] || 0) + (Number(e.amount) || 0);
        });
        return map;
    }, [transactions, operationalExpenses]);

    // ── Real-time expense streak (computed from data) ─────────────
    const expenseStreak = useMemo(() => {
        if (dailyCap <= 0) return 0;
        let streak = 0;
        const today = new Date();
        for (let i = 1; i <= 60; i++) {
            const d = new Date(today);
            d.setDate(d.getDate() - i);
            const key = d.toISOString().slice(0, 10);
            const spent = dailyExpenseMap[key];
            if (spent === undefined) continue; // skip days with no data
            if (spent <= dailyCap) streak++;
            else break;
        }
        return streak;
    }, [dailyExpenseMap, dailyCap]);

    const achievementData = useMemo(() => {
        if (dailyCap <= 0) return [];
        const points = [];
        const today = new Date();
        for (let i = 13; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(d.getDate() - i);
            const key = d.toISOString().slice(0, 10);
            const spent = dailyExpenseMap[key] || 0;
            points.push({ label: `${d.getDate()}/${d.getMonth() + 1}`, spent, target: dailyCap, hit: spent <= dailyCap ? 1 : 0 });
        }
        return points;
    }, [dailyExpenseMap, dailyCap]);

    // ── Upcoming dues — only unpaid items ─────────────────────────
    const upcomingDues = useMemo(() => {
        const dues = [];
        // Fixed: only where current month status is NOT paid and NOT skipped
        (fixedExpenses || []).filter(e => {
            const status = (e.monthlyStatus || {})[currentMonth];
            return e.isActive !== false && status !== 'paid' && status !== 'skipped';
        }).forEach(e => {
            const dueDate = new Date(now.getFullYear(), now.getMonth(), e.dueDay || 1);
            if (dueDate < now) dueDate.setMonth(dueDate.getMonth() + 1);
            dues.push({ label: e.label, amount: e.amount, date: dueDate, type: 'fixed', id: e.id });
        });
        // EMIs: only where current month status is NOT paid
        (emis || []).filter(e => {
            const status = (e.monthlyStatus || {})[currentMonth];
            return status !== 'paid';
        }).forEach(e => {
            if (e.dueDate) {
                const d = new Date(e.dueDate);
                if (d >= now) dues.push({ label: e.label, amount: e.monthlyAmount, date: d, type: 'emi', id: e.id });
            }
        });
        return dues.sort((a, b) => a.date - b.date).slice(0, 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fixedExpenses, emis, currentMonth]);

    // ── Overspent categories ──────────────────────────────────────
    const overspentCategories = useMemo(() => {
        const budgets = settings?.categoryBudgets || {};
        return categoryData.filter(c => budgets[c.name] && c.value > budgets[c.name])
            .map(c => ({ ...c, budget: budgets[c.name], excess: c.value - budgets[c.name] }));
    }, [categoryData, settings]);

    // ── Forecast ──────────────────────────────────────────────────
    const forecast = useMemo(() => {
        if (calendarMode === 'day') return null;
        const daysPassed = calendarMode === 'week'
            ? Math.max(1, now.getDay() === 0 ? 7 : now.getDay())
            : Math.max(1, now.getDate());
        const totalDays = calendarMode === 'week' ? 7 : new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        const dailyRate = opAndTxnTotal / daysPassed;
        return Math.round(dailyRate * totalDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opAndTxnTotal, calendarMode]);

    // ── CRUD handlers ─────────────────────────────────────────────
    const handleDeleteFixed = async (id) => { if (window.confirm('Delete?')) await deleteFromCollection('fixedExpenses', id); };
    const handleDeleteEMI = async (id) => { if (window.confirm('Delete?')) await deleteFromCollection('emis', id); };
    const handleDeleteOp = async (id) => { if (window.confirm('Delete?')) await deleteFromCollection('operationalExpenses', id); };

    // Monthly status handlers for fixed expenses
    const handleSetFixedStatus = async (item, status) => {
        const newStatus = { ...(item.monthlyStatus || {}), [currentMonth]: status };
        await updateInCollection('fixedExpenses', item.id, { monthlyStatus: newStatus });
    };

    // Monthly status handlers for EMIs
    const handleSetEMIStatus = async (item, status) => {
        const newStatus = { ...(item.monthlyStatus || {}), [currentMonth]: status };
        const updates = { monthlyStatus: newStatus };
        const prevStatus = (item.monthlyStatus || {})[currentMonth];
        if (status === 'paid' && prevStatus !== 'paid') {
            updates.paidMonths = (item.paidMonths || 0) + 1;
        } else if (status !== 'paid' && prevStatus === 'paid') {
            updates.paidMonths = Math.max(0, (item.paidMonths || 0) - 1);
        }
        await updateInCollection('emis', item.id, updates);
    };

    const handleAddOp = useCallback(async (e) => {
        e.preventDefault();
        if (!opForm.label || !opForm.amount) return;
        setOpSaving(true);
        await addToCollection('operationalExpenses', {
            label: opForm.label, amount: Number(opForm.amount), category: opForm.category,
            date: new Date(opForm.date + 'T12:00:00').toISOString(),
        });
        setOpForm({ label: '', amount: '', category: 'Food & Grocery', date: new Date().toISOString().slice(0, 10) });
        setOpSaving(false);
    }, [opForm]);

    return (
        <div className="space-y-6">
            {/* Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <div className={L.card}>
                    <p className={L.textLabel}>Total Expenses</p>
                    <p className={`text-2xl font-bold ${L.negative} mt-1`}>{'\u20B9'}{totalPeriodExpenses.toLocaleString()}</p>
                    <p className={`text-[10px] ${L.textMuted} mt-1`}>All categories combined</p>
                </div>
                <div className={L.card}>
                    <p className={L.textLabel}>Fixed/Month</p>
                    <p className={`text-2xl font-bold ${L.text} mt-1`}>{'\u20B9'}{totalFixedMonthly.toLocaleString()}</p>
                    <p className={`text-[10px] ${L.textMuted} mt-1 leading-relaxed`}>
                        <span className="text-emerald-600">{fixedBreakdown.paid.length} paid ({'\u20B9'}{fixedBreakdown.paidTotal.toLocaleString()})</span>
                        {' · '}
                        <span className="text-amber-600">{fixedBreakdown.pending.length} pending ({'\u20B9'}{fixedBreakdown.pendingTotal.toLocaleString()})</span>
                        {fixedBreakdown.skipped.length > 0 && <>
                            {' · '}
                            <span className="text-gray-400">{fixedBreakdown.skipped.length} skipped ({'\u20B9'}{fixedBreakdown.skippedTotal.toLocaleString()})</span>
                        </>}
                    </p>
                </div>
                <div className={L.card}>
                    <p className={L.textLabel}>EMIs/Month</p>
                    <p className={`text-2xl font-bold ${L.text} mt-1`}>{'\u20B9'}{totalEMIMonthly.toLocaleString()}</p>
                    <p className={`text-[10px] ${L.textMuted} mt-1`}>
                        <span className="text-emerald-600">{emiBreakdown.paid.length} paid ({'\u20B9'}{emiBreakdown.paidTotal.toLocaleString()})</span>
                        {' · '}
                        <span className="text-amber-600">{emiBreakdown.pending.length} pending ({'\u20B9'}{emiBreakdown.pendingTotal.toLocaleString()})</span>
                    </p>
                </div>
                <div className={L.card}>
                    <p className={L.textLabel}>One-Time Payments</p>
                    <p className={`text-2xl font-bold text-indigo-600 mt-1`}>{'\u20B9'}{periodOneTimeTotal.toLocaleString()}</p>
                    <p className={`text-[10px] ${L.textMuted} mt-1`}>
                        {wishlistWithPayments.length} item{wishlistWithPayments.length !== 1 ? 's' : ''} {'\u00B7'} {'\u20B9'}{oneTimeTotalPaid.toLocaleString()} total paid
                    </p>
                </div>
                <div className={L.card}>
                    <p className={L.textLabel}>Operational</p>
                    <p className={`text-2xl font-bold text-purple-600 mt-1`}>{'\u20B9'}{opAndTxnTotal.toLocaleString()}</p>
                    <p className={`text-[10px] ${L.textMuted} mt-1`}>Daily + bank expenses</p>
                </div>
                <div className={L.card}>
                    <p className={L.textLabel}>EBITDA</p>
                    <p className={`text-2xl font-bold ${ebitda >= 0 ? L.positive : L.negative} mt-1`}>
                        {ebitda < 0 ? '-' : ''}{'\u20B9'}{Math.abs(ebitda).toLocaleString()}
                    </p>
                </div>
            </div>

            {/* Savings Pool Card */}
            <div className={`${L.card} border ${totalSavings >= 0 ? 'border-emerald-100 bg-emerald-50/30' : 'border-rose-100 bg-rose-50/30'}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${totalSavings >= 0 ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                            <FaPiggyBank className={`text-lg ${totalSavings >= 0 ? 'text-emerald-600' : 'text-rose-500'}`} />
                        </div>
                        <div>
                            <p className={L.textLabel}>Savings Pool</p>
                            <p className={`text-2xl font-bold ${totalSavings >= 0 ? 'text-emerald-600' : 'text-rose-500'} mt-0.5`}>
                                {totalSavings < 0 ? '-' : ''}{'\u20B9'}{Math.abs(totalSavings).toLocaleString()}
                            </p>
                        </div>
                    </div>
                    <div className="text-right space-y-1">
                        <p className="text-[10px] text-gray-400">
                            <FaWallet className="inline mr-1 text-blue-400" />Income: {'\u20B9'}{totalPeriodIncome.toLocaleString()}
                        </p>
                        <p className="text-[10px] text-gray-400">
                            Allocated: {'\u20B9'}{totalActiveAllocations.toLocaleString()}
                        </p>
                        <p className="text-[10px] text-gray-400">
                            Operational: {'\u20B9'}{periodOpExpenses.toLocaleString()}
                        </p>
                    </div>
                </div>
            </div>

            {/* Expense Sub-tabs */}
            <div className={L.card}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
                    <h3 className={`text-base font-bold ${L.text}`}>Expense Management</h3>
                    <div className="flex bg-gray-100 rounded-xl p-1">
                        {[
                            { key: 'fixed', label: 'Fixed', icon: <FaCalendarAlt className="text-[9px]" />, count: fixedExpenses?.length || 0 },
                            { key: 'emi', label: 'EMIs', icon: <FaCreditCard className="text-[9px]" />, count: emis?.length || 0 },
                            { key: 'operational', label: 'Operational', icon: <FaBolt className="text-[9px]" />, count: operationalExpenses?.length || 0 },
                            { key: 'onetime', label: 'One-Time', icon: <FaGem className="text-[9px]" />, count: wishlistWithPayments.length },
                        ].map(tab => (
                            <button key={tab.key} onClick={() => setExpSubTab(tab.key)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                                    expSubTab === tab.key ? L.tabActive : L.tabInactive
                                }`}>
                                {tab.icon} {tab.label} <span className="opacity-60">{tab.count}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Fixed Tab */}
                {expSubTab === 'fixed' && (
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <p className={`text-xs ${L.textMuted}`}>Total: {'\u20B9'}{totalFixedMonthly.toLocaleString()}/month</p>
                            <button onClick={() => { setEditItem(null); setShowFixedForm(true); }} className={`${L.btnPrimary} text-xs flex items-center gap-2`}><FaPlus className="text-[10px]" /> Add</button>
                        </div>
                        {(!fixedExpenses || fixedExpenses.length === 0) ? (
                            <div className="text-center py-10"><FaCalendarAlt className="text-3xl text-gray-300 mx-auto mb-3" /><p className={`text-sm ${L.textMuted}`}>No fixed expenses</p></div>
                        ) : (
                            <div className="space-y-2">
                                {fixedExpenses.map(fe => {
                                    const monthStatus = (fe.monthlyStatus || {})[currentMonth] || 'unpaid';
                                    return (
                                        <div key={fe.id} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 group hover:shadow-sm transition-all">
                                            <div className="flex items-center gap-3">
                                                <div className="flex gap-1">
                                                    <button onClick={() => handleSetFixedStatus(fe, monthStatus === 'paid' ? 'unpaid' : 'paid')}
                                                        title={monthStatus === 'paid' ? 'Mark unpaid' : 'Mark paid'}
                                                        className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs transition-all ${
                                                            monthStatus === 'paid'
                                                                ? 'bg-emerald-100 text-emerald-600 border border-emerald-200'
                                                                : 'bg-gray-100 text-gray-400 border border-gray-200 hover:bg-emerald-50 hover:text-emerald-500'
                                                        }`}><FaCheck /></button>
                                                    <button onClick={() => handleSetFixedStatus(fe, monthStatus === 'skipped' ? 'unpaid' : 'skipped')}
                                                        title={monthStatus === 'skipped' ? 'Mark unpaid' : 'Skip this month'}
                                                        className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs transition-all ${
                                                            monthStatus === 'skipped'
                                                                ? 'bg-amber-100 text-amber-600 border border-amber-200'
                                                                : 'bg-gray-100 text-gray-400 border border-gray-200 hover:bg-amber-50 hover:text-amber-500'
                                                        }`}><FaBan /></button>
                                                </div>
                                                <div>
                                                    <p className={`text-sm font-semibold ${monthStatus === 'paid' ? 'text-gray-400 line-through' : monthStatus === 'skipped' ? 'text-gray-400 italic' : L.text}`}>{fe.label}</p>
                                                    <p className="text-[10px] text-gray-400">
                                                        {fe.category} {'\u00B7'} Due day {fe.dueDay}
                                                        {monthStatus === 'paid' && <span className="ml-1.5 text-emerald-500 font-semibold">Paid</span>}
                                                        {monthStatus === 'skipped' && <span className="ml-1.5 text-amber-500 font-semibold">Skipped</span>}
                                                    </p>
                                                    {allocationsByItem[fe.id] && (() => {
                                                        const info = allocationsByItem[fe.id];
                                                        const feAmt = Number(fe.amount) || 0;
                                                        const pct = feAmt > 0 ? ((info.totalFunded / feAmt) * 100).toFixed(0) : 0;
                                                        return (
                                                            <p className="text-[10px] text-blue-600 mt-0.5">
                                                                Funded: {'\u20B9'}{info.totalFunded.toLocaleString()} / {'\u20B9'}{feAmt.toLocaleString()} ({pct}%)
                                                                {info.sources.length > 0 && <span className="text-gray-400"> — from {info.sources.map(s => s.label).join(', ')}</span>}
                                                            </p>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <p className={`text-sm font-bold ${L.text}`}>{'\u20B9'}{Number(fe.amount).toLocaleString()}</p>
                                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                    <button onClick={() => { setEditItem(fe); setShowFixedForm(true); }} className="p-1 text-gray-400 hover:text-blue-600"><FaPen className="text-[9px]" /></button>
                                                    <button onClick={() => handleDeleteFixed(fe.id)} className="p-1 text-gray-400 hover:text-rose-500"><FaTrash className="text-[9px]" /></button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* EMI Tab */}
                {expSubTab === 'emi' && (
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <p className={`text-xs ${L.textMuted}`}>Total: {'\u20B9'}{totalEMIMonthly.toLocaleString()}/month</p>
                            <button onClick={() => { setEditItem(null); setShowEMIForm(true); }} className={`${L.btnPrimary} text-xs flex items-center gap-2`}><FaPlus className="text-[10px]" /> Add EMI</button>
                        </div>
                        {(!emis || emis.length === 0) ? (
                            <div className="text-center py-10"><FaCreditCard className="text-3xl text-gray-300 mx-auto mb-3" /><p className={`text-sm ${L.textMuted}`}>No EMIs</p></div>
                        ) : (
                            <div className="space-y-3">
                                {emis.map(e => {
                                    const progress = e.totalTenure > 0 ? ((e.paidMonths || 0) / e.totalTenure) * 100 : 0;
                                    const monthStatus = (e.monthlyStatus || {})[currentMonth] || 'unpaid';
                                    return (
                                        <div key={e.id} className="p-4 rounded-xl border border-gray-100 group hover:shadow-sm transition-all">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex gap-1">
                                                        <button onClick={() => handleSetEMIStatus(e, monthStatus === 'paid' ? 'unpaid' : 'paid')}
                                                            title={monthStatus === 'paid' ? 'Mark unpaid' : 'Mark paid'}
                                                            className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs transition-all ${
                                                                monthStatus === 'paid'
                                                                    ? 'bg-emerald-100 text-emerald-600 border border-emerald-200'
                                                                    : 'bg-gray-100 text-gray-400 border border-gray-200 hover:bg-emerald-50 hover:text-emerald-500'
                                                            }`}><FaCheck /></button>
                                                    </div>
                                                    <div>
                                                        <h4 className={`font-semibold text-sm ${monthStatus === 'paid' ? 'text-gray-400 line-through' : L.text}`}>{e.label}</h4>
                                                        <p className="text-[10px] text-gray-400">
                                                            {monthStatus === 'paid' && <span className="text-emerald-500 font-semibold">Paid this month</span>}
                                                            {monthStatus !== 'paid' && <span className="text-amber-500 font-semibold">Unpaid this month</span>}
                                                        </p>
                                                        {allocationsByItem[e.id] && (() => {
                                                            const info = allocationsByItem[e.id];
                                                            const emiAmt = Number(e.monthlyAmount) || 0;
                                                            const pct = emiAmt > 0 ? ((info.totalFunded / emiAmt) * 100).toFixed(0) : 0;
                                                            return (
                                                                <p className="text-[10px] text-blue-600 mt-0.5">
                                                                    Funded: {'\u20B9'}{info.totalFunded.toLocaleString()} / {'\u20B9'}{emiAmt.toLocaleString()} ({pct}%)
                                                                    {info.sources.length > 0 && <span className="text-gray-400"> — from {info.sources.map(s => s.label).join(', ')}</span>}
                                                                </p>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <p className={`text-lg font-bold ${L.text}`}>{'\u20B9'}{Number(e.monthlyAmount).toLocaleString()}/mo</p>
                                                    <button onClick={() => handleDeleteEMI(e.id)} className="p-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-rose-500 transition-all"><FaTrash className="text-[10px]" /></button>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                    <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
                                                </div>
                                                <span className="text-[10px] text-gray-500">{(e.paidMonths || 0)}/{e.totalTenure} months</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Operational Tab */}
                {expSubTab === 'operational' && (
                    <div>
                        <div className="flex items-center gap-2 mb-3 px-1">
                            <FaPiggyBank className="text-xs text-amber-500" />
                            <p className="text-[10px] text-amber-600 font-semibold">All operational expenses are deducted from your savings pool</p>
                        </div>
                        <form onSubmit={handleAddOp} className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
                            <input type="text" placeholder="What?" value={opForm.label} onChange={e => setOpForm({ ...opForm, label: e.target.value })} required
                                className={`col-span-2 md:col-span-1 ${L.input}`} />
                            <input type="number" placeholder="Amount" value={opForm.amount} onChange={e => setOpForm({ ...opForm, amount: e.target.value })} required className={L.input} />
                            <select value={opForm.category} onChange={e => setOpForm({ ...opForm, category: e.target.value })} className={L.input}>
                                {OPERATIONAL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <input type="date" value={opForm.date} onChange={e => setOpForm({ ...opForm, date: e.target.value })} className={L.input} />
                            <button type="submit" disabled={opSaving} className={L.btnPrimary}>{opSaving ? '...' : '+ Add'}</button>
                        </form>
                        {(!operationalExpenses || operationalExpenses.length === 0) ? (
                            <div className="text-center py-10"><FaBolt className="text-3xl text-gray-300 mx-auto mb-3" /><p className={`text-sm ${L.textMuted}`}>No expenses logged</p></div>
                        ) : (
                            <div className="space-y-2 max-h-[350px] overflow-y-auto">
                                {operationalExpenses.map(op => (
                                    <div key={op.id} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 group hover:shadow-sm transition-all">
                                        <div className="flex items-center gap-3">
                                            <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center text-xs text-gray-500">{OP_CAT_ICONS[op.category] || <FaEllipsisH />}</div>
                                            <div>
                                                <p className={`text-sm font-semibold ${L.text}`}>{op.label}</p>
                                                <p className="text-[10px] text-gray-400">{op.category} {'\u00B7'} {op.date ? new Date(op.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <p className={`text-sm font-bold ${L.negative}`}>-{'\u20B9'}{Number(op.amount).toLocaleString()}</p>
                                            <button onClick={() => handleDeleteOp(op.id)} className="p-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-rose-500 transition-all"><FaTrash className="text-[9px]" /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* One-Time Tab */}
                {expSubTab === 'onetime' && (
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <p className={`text-xs ${L.textMuted}`}>
                                    Vision board payments {'\u00B7'} This period: {'\u20B9'}{periodOneTimeTotal.toLocaleString()}
                                </p>
                                <p className="text-[10px] text-gray-400 mt-0.5">
                                    Included in total expenses {'\u00B7'} Does not affect daily expense targets
                                </p>
                            </div>
                        </div>
                        {wishlistWithPayments.length === 0 ? (
                            <div className="text-center py-10">
                                <FaGem className="text-3xl text-gray-300 mx-auto mb-3" />
                                <p className={`text-sm ${L.textMuted}`}>No one-time payments yet</p>
                                <p className="text-[10px] text-gray-400 mt-1">Make payments in Vision Board tab to track them here</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {wishlistWithPayments.map(item => {
                                    const paid = Number(item.totalPaid) || 0;
                                    const target = Number(item.amount) || 0;
                                    const pct = target > 0 ? (paid / target) * 100 : 0;
                                    const isComplete = item.status === 'completed';
                                    return (
                                        <div key={item.id} className={`p-3 rounded-xl border ${isComplete ? 'border-emerald-100 bg-emerald-50/30' : 'border-gray-100'}`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    {item.imageUrl ? (
                                                        <img src={item.imageUrl} alt={item.name} className="w-9 h-9 rounded-lg object-cover" />
                                                    ) : (
                                                        <div className={`w-9 h-9 ${isComplete ? 'bg-emerald-100' : 'bg-indigo-100'} rounded-lg flex items-center justify-center`}>
                                                            <FaGem className={`${isComplete ? 'text-emerald-500' : 'text-indigo-500'} text-sm`} />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className={`text-sm font-semibold ${L.text}`}>{item.name}</p>
                                                        <p className="text-[10px] text-gray-400">
                                                            {(item.payments || []).length} payment{(item.payments || []).length !== 1 ? 's' : ''}
                                                            {' \u00B7 '}
                                                            {isComplete
                                                                ? <span className="text-emerald-600 font-semibold">Completed</span>
                                                                : <span className="text-amber-600 font-semibold">{'\u20B9'}{(target - paid).toLocaleString()} remaining</span>
                                                            }
                                                        </p>
                                                        {allocationsByItem[item.id] && (() => {
                                                            const info = allocationsByItem[item.id];
                                                            const pctFunded = target > 0 ? ((info.totalFunded / target) * 100).toFixed(0) : 0;
                                                            return (
                                                                <p className="text-[10px] text-blue-600 mt-0.5">
                                                                    Funded: {'\u20B9'}{info.totalFunded.toLocaleString()} / {'\u20B9'}{target.toLocaleString()} ({pctFunded}%)
                                                                    {info.sources.length > 0 && <span className="text-gray-400"> — from {info.sources.map(s => s.label).join(', ')}</span>}
                                                                </p>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className={`text-sm font-bold ${L.text}`}>{'\u20B9'}{paid.toLocaleString()}</p>
                                                    <p className="text-[10px] text-gray-400">of {'\u20B9'}{target.toLocaleString()}</p>
                                                </div>
                                            </div>
                                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                <div className={`h-full rounded-full transition-all duration-700 ${isComplete ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                                                    style={{ width: `${Math.min(100, pct)}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Expense Trend Chart */}
            <div className={L.card}>
                <h3 className={`text-base font-bold ${L.text} mb-4`}>Expense Trend</h3>
                {trendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={trendData}>
                            <defs>
                                <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.2} />
                                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="label" tick={{ fill: '#6B7280', fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `\u20B9${(v / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => `\u20B9${v.toLocaleString()}`} contentStyle={LIGHT_TOOLTIP} />
                            {dailyCap > 0 && <ReferenceLine y={dailyCap} stroke="#F59E0B" strokeDasharray="5 5" label={{ value: 'Cap', fill: '#F59E0B', fontSize: 10 }} />}
                            <Area type="monotone" dataKey="expense" stroke="#EF4444" fill="url(#expGrad)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <p className={`text-sm ${L.textMuted} text-center py-10`}>No data for this period</p>
                )}
            </div>

            {/* Category Breakdown + Top Spenders */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {categoryData.length > 0 && (
                    <div className={L.card}>
                        <h3 className={`text-sm font-bold ${L.text} mb-4`}>Category Breakdown</h3>
                        <ResponsiveContainer width="100%" height={Math.max(180, Math.min(categoryData.length, 8) * 36)}>
                            <BarChart layout="vertical" data={categoryData.slice(0, 8)} margin={{ left: 10 }}>
                                <XAxis type="number" tick={{ fill: '#6B7280', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `\u20B9${(v / 1000).toFixed(0)}k`} />
                                <YAxis type="category" dataKey="name" tick={{ fill: '#6B7280', fontSize: 11 }} axisLine={false} tickLine={false} width={100} />
                                <Tooltip formatter={(v) => `\u20B9${v.toLocaleString()}`} contentStyle={LIGHT_TOOLTIP} />
                                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                                    {categoryData.slice(0, 8).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                <div className={L.card}>
                    <h3 className={`text-sm font-bold ${L.text} mb-3`}>Top Spenders</h3>
                    {categoryData.length > 0 ? (
                        <div className="space-y-3">
                            {categoryData.slice(0, 5).map((cat, i) => (
                                <div key={cat.name} className="flex items-center gap-3">
                                    <span className="text-[10px] font-bold text-gray-400 w-4">{i + 1}</span>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className={`text-xs font-semibold ${L.text}`}>{cat.name}</span>
                                            <span className={`text-xs ${L.textMuted}`}>{'\u20B9'}{cat.value.toLocaleString()} ({totalCategorySpend > 0 ? ((cat.value / totalCategorySpend) * 100).toFixed(0) : 0}%)</span>
                                        </div>
                                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${totalCategorySpend > 0 ? (cat.value / totalCategorySpend) * 100 : 0}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className={`text-sm ${L.textMuted} text-center py-6`}>No data yet</p>
                    )}
                </div>
            </div>

            {/* Expense Targets */}
            <div className={L.card}>
                <div className="flex items-center justify-between mb-5">
                    <h3 className={`text-base font-bold ${L.text}`}>Expense Targets</h3>
                    <button onClick={() => setShowTargetForm(true)} className={`${L.btnSecondary} flex items-center gap-2`}><FaCog className="text-[10px]" /> Set Cap</button>
                </div>

                {dailyCap > 0 ? (
                    <div className="space-y-5">
                        <div className="grid grid-cols-3 gap-3">
                            {[{ label: 'Daily Cap', target: dailyCap }, { label: 'Weekly Cap', target: weeklyCap }, { label: 'Monthly Cap', target: monthlyCap }].map(t => (
                                <div key={t.label} className="p-4 bg-gray-50 rounded-xl text-center">
                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest">{t.label}</p>
                                    <p className={`text-lg font-bold ${L.text} mt-1`}>{'\u20B9'}{t.target.toLocaleString()}</p>
                                </div>
                            ))}
                        </div>

                        <div className={`p-5 rounded-xl border ${targetPct > 100 ? 'bg-rose-50 border-rose-100' : targetPct > 75 ? 'bg-amber-50 border-amber-100' : 'bg-emerald-50 border-emerald-100'}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest">Progress</p>
                                    <p className={`text-2xl font-bold ${targetPct > 100 ? 'text-rose-600' : targetPct > 75 ? 'text-amber-600' : 'text-emerald-600'} mt-1`}>
                                        {Math.min(100, targetPct).toFixed(0)}% used
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] text-gray-400 uppercase">Remaining</p>
                                    <p className={`text-xl font-bold ${targetRemaining >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                        {targetRemaining >= 0 ? `\u20B9${targetRemaining.toLocaleString()}` : `Exceeded by \u20B9${Math.abs(targetRemaining).toLocaleString()}`}
                                    </p>
                                </div>
                            </div>
                            <div className="h-2 bg-white rounded-full overflow-hidden mt-3">
                                <div className={`h-full rounded-full transition-all duration-700 ${targetPct > 100 ? 'bg-rose-500' : targetPct > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                    style={{ width: `${Math.min(100, targetPct)}%` }} />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 bg-orange-50 rounded-xl border border-orange-100 text-center">
                                <FaFire className="text-2xl text-orange-500 mx-auto mb-2" />
                                <p className="text-2xl font-bold text-orange-600">{expenseStreak}</p>
                                <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">Day Streak Under Budget</p>
                            </div>
                            {forecast !== null && (
                                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 text-center">
                                    <FaBullseye className="text-2xl text-blue-500 mx-auto mb-2" />
                                    <p className="text-2xl font-bold text-blue-600">{'\u20B9'}{forecast.toLocaleString()}</p>
                                    <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">Projected {calendarMode === 'week' ? 'Weekly' : 'Monthly'}</p>
                                </div>
                            )}
                        </div>

                        {achievementData.length > 0 && (
                            <div>
                                <h4 className={`text-sm font-bold ${L.text} mb-3`}>Under-Budget Achievement (14 days)</h4>
                                <ResponsiveContainer width="100%" height={120}>
                                    <BarChart data={achievementData}>
                                        <XAxis dataKey="label" tick={{ fill: '#6B7280', fontSize: 9 }} axisLine={false} tickLine={false} />
                                        <Tooltip formatter={(v) => `\u20B9${v.toLocaleString()}`} contentStyle={LIGHT_TOOLTIP} />
                                        <Bar dataKey="spent" radius={[4, 4, 0, 0]}>
                                            {achievementData.map((d, i) => <Cell key={i} fill={d.hit ? '#10B981' : '#EF4444'} />)}
                                        </Bar>
                                        <ReferenceLine y={dailyCap} stroke="#F59E0B" strokeDasharray="4 4" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <FaBullseye className="text-3xl text-gray-300 mx-auto mb-3" />
                        <p className={`text-sm ${L.textMuted}`}>Set a daily expense cap to track targets</p>
                        <button onClick={() => setShowTargetForm(true)} className={`${L.btnPrimary} mt-3`}>Set Cap</button>
                    </div>
                )}
            </div>

            {/* Upcoming Dues Timeline */}
            {upcomingDues.length > 0 && (
                <div className={L.card}>
                    <h3 className={`text-base font-bold ${L.text} mb-4`}>Upcoming Dues (Unpaid Only)</h3>
                    <div className="relative pl-6">
                        <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gray-200 rounded-full" />
                        {upcomingDues.map((due, i) => {
                            const daysAway = Math.ceil((due.date - new Date()) / 86400000);
                            const isUrgent = daysAway <= 3;
                            return (
                                <div key={`${due.id}-${i}`} className="relative mb-4 last:mb-0">
                                    <div className={`absolute -left-4 top-2 w-3 h-3 rounded-full border-2 ${isUrgent ? 'bg-rose-500 border-rose-300' : 'bg-blue-500 border-blue-300'}`} />
                                    <div className={`p-3 rounded-xl border ${isUrgent ? 'border-rose-100 bg-rose-50' : 'border-gray-100 bg-gray-50'}`}>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className={`text-sm font-semibold ${L.text}`}>{due.label}</p>
                                                <p className="text-[10px] text-gray-400">{due.type === 'emi' ? 'EMI' : 'Fixed'} {'\u00B7'} {due.date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className={`text-sm font-bold ${L.text}`}>{'\u20B9'}{Number(due.amount).toLocaleString()}</p>
                                                <p className={`text-[10px] ${isUrgent ? 'text-rose-500 font-semibold' : 'text-gray-400'}`}>
                                                    {daysAway === 0 ? 'Today' : daysAway === 1 ? 'Tomorrow' : `${daysAway} days`}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Overspent Analytics */}
            {overspentCategories.length > 0 && (
                <div className={`${L.card} border-rose-100 bg-rose-50/50`}>
                    <div className="flex items-center gap-2 mb-4">
                        <FaExclamationTriangle className="text-rose-500" />
                        <h3 className={`text-base font-bold ${L.text}`}>Overspent Categories</h3>
                    </div>
                    <div className="space-y-3">
                        {overspentCategories.map(cat => (
                            <div key={cat.name} className="p-3 bg-white rounded-xl border border-rose-100">
                                <div className="flex items-center justify-between mb-1">
                                    <span className={`text-sm font-semibold ${L.text}`}>{cat.name}</span>
                                    <span className="text-sm font-bold text-rose-600">+{'\u20B9'}{cat.excess.toLocaleString()} over</span>
                                </div>
                                <p className="text-[10px] text-gray-500">Budget: {'\u20B9'}{cat.budget.toLocaleString()} | Actual: {'\u20B9'}{cat.value.toLocaleString()}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Modals */}
            {showFixedForm && (
                <div className={L.modal} onClick={() => { setShowFixedForm(false); setEditItem(null); }}>
                    <div className={L.modalCard} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className={`text-lg font-bold ${L.text}`}>{editItem ? 'Edit Fixed Expense' : 'Add Fixed Expense'}</h3>
                            <button onClick={() => { setShowFixedForm(false); setEditItem(null); }} className="p-2 text-gray-400 hover:text-gray-700"><FaTimes /></button>
                        </div>
                        <FixedExpenseForm L={L} item={editItem} onSave={async (data) => {
                            if (editItem) await updateInCollection('fixedExpenses', editItem.id, data);
                            else await addToCollection('fixedExpenses', data);
                            setShowFixedForm(false); setEditItem(null);
                        }} />
                    </div>
                </div>
            )}

            {showEMIForm && (
                <div className={L.modal} onClick={() => setShowEMIForm(false)}>
                    <div className={L.modalCard} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className={`text-lg font-bold ${L.text}`}>Add EMI</h3>
                            <button onClick={() => setShowEMIForm(false)} className="p-2 text-gray-400 hover:text-gray-700"><FaTimes /></button>
                        </div>
                        <EMIForm L={L} onSave={async (data) => {
                            await addToCollection('emis', data);
                            setShowEMIForm(false);
                        }} />
                    </div>
                </div>
            )}

            {showTargetForm && (
                <div className={L.modal} onClick={() => setShowTargetForm(false)}>
                    <div className={L.modalCard} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className={`text-lg font-bold ${L.text}`}>Set Expense Cap</h3>
                            <button onClick={() => setShowTargetForm(false)} className="p-2 text-gray-400 hover:text-gray-700"><FaTimes /></button>
                        </div>
                        <ExpenseCapForm L={L} currentCap={dailyCap} onSave={async (val) => {
                            await updateSpendingTargets({ dailyCap: Number(val), weeklyCap: Number(val) * 7, monthlyCap: Number(val) * 30 });
                            setShowTargetForm(false);
                        }} />
                    </div>
                </div>
            )}
        </div>
    );
};

// Form Components
const FixedExpenseForm = ({ L, item, onSave }) => {
    const [form, setForm] = useState({
        label: item?.label || '', amount: item?.amount || '', category: item?.category || 'Other',
        dueDay: item?.dueDay || 1, isActive: item?.isActive !== false,
    });
    const [saving, setSaving] = useState(false);
    const handleSubmit = async (e) => { e.preventDefault(); setSaving(true); await onSave({ ...form, amount: Number(form.amount), dueDay: Number(form.dueDay) }); setSaving(false); };
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <input type="text" placeholder="Expense name" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} required className={L.input} />
            <input type="number" placeholder="Monthly amount" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required className={L.input} />
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className={L.input}>
                {FIXED_EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="number" min="1" max="31" placeholder="Due day (1-31)" value={form.dueDay} onChange={e => setForm({ ...form, dueDay: e.target.value })} className={L.input} />
            <button type="submit" disabled={saving} className={`w-full py-3 ${L.btnPrimary}`}>{saving ? 'Saving...' : item ? 'Update' : 'Add'}</button>
        </form>
    );
};

const EMIForm = ({ L, onSave }) => {
    const [form, setForm] = useState({ label: '', monthlyAmount: '', totalTenure: '', paidMonths: 0, dueDate: '' });
    const [saving, setSaving] = useState(false);
    const handleSubmit = async (e) => { e.preventDefault(); setSaving(true); await onSave({ ...form, monthlyAmount: Number(form.monthlyAmount), totalTenure: Number(form.totalTenure), paidMonths: Number(form.paidMonths) }); setSaving(false); };
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <input type="text" placeholder="EMI label" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} required className={L.input} />
            <div className="grid grid-cols-2 gap-3">
                <input type="number" placeholder="Monthly amount" value={form.monthlyAmount} onChange={e => setForm({ ...form, monthlyAmount: e.target.value })} required className={L.input} />
                <input type="number" placeholder="Tenure (months)" value={form.totalTenure} onChange={e => setForm({ ...form, totalTenure: e.target.value })} required className={L.input} />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <input type="number" placeholder="Months paid" value={form.paidMonths} onChange={e => setForm({ ...form, paidMonths: e.target.value })} className={L.input} />
                <input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} className={L.input} />
            </div>
            <button type="submit" disabled={saving} className={`w-full py-3 ${L.btnPrimary}`}>{saving ? 'Saving...' : 'Add EMI'}</button>
        </form>
    );
};

const ExpenseCapForm = ({ L, currentCap, onSave }) => {
    const [value, setValue] = useState(currentCap || '');
    const [saving, setSaving] = useState(false);
    const handleSubmit = async (e) => { e.preventDefault(); setSaving(true); await onSave(value); setSaving(false); };
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <p className={`text-xs ${L.textMuted}`}>Set daily expense cap. Weekly and monthly auto-calculate.</p>
            <div>
                <label className="text-[10px] text-gray-400 uppercase tracking-widest mb-1 block">Daily Expense Cap</label>
                <input type="number" placeholder="e.g., 2000" value={value} onChange={e => setValue(e.target.value)} className={L.input} />
            </div>
            {value > 0 && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="text-center">
                        <p className="text-[10px] text-gray-400 uppercase">Weekly Cap</p>
                        <p className={`text-sm font-bold ${L.text}`}>{'\u20B9'}{(Number(value) * 7).toLocaleString()}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-[10px] text-gray-400 uppercase">Monthly Cap</p>
                        <p className={`text-sm font-bold ${L.text}`}>{'\u20B9'}{(Number(value) * 30).toLocaleString()}</p>
                    </div>
                </div>
            )}
            <button type="submit" disabled={saving} className={`w-full py-3 ${L.btnPrimary}`}>{saving ? 'Saving...' : 'Save Cap'}</button>
        </form>
    );
};

export default ExpensesTab;
